<template>
    <tailwind-layout title="Blog Posts">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <div>
                  <h1 class="font-semibold text-2xl leading-tight">Share Your Expertise with the RentRisk Community</h1>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link class="primaryButton" :href="route('blogs.create')">
                        Add Blog Post
                    </inertia-link>
                </div>
            </div>
            <div class="px-4 sm:px-6 lg:px-8 text-xs space-y-4 w-10/12">
              <p>
                At RentRisk, we give our agent and landlord partners a platform to share insights and updates about the rental market. By publishing blog posts on our site, you can attract new leads or tenants, build credibility, boost your personal SEO, stand out from the competition, and educate potential clients.
              </p>

              <p class="font-semibold">To get started:</p>

              <ol class="list-decimal list-inside space-y-1">
                <li>
                  Click on the <span class="font-medium">"My Profile"</span> tab in the top right corner of your dashboard.
                </li>
                <li>
                  Add your social media links, website, and profile picture to complete your profile.
                </li>
                <li>
                  Once your profile is set up, you can:
                  <ul class="list-disc list-inside ml-4 space-y-1">
                    <li>Write a blog post</li>
                    <li>Upload an image</li>
                    <li>Submit your article for approval</li>
                  </ul>
                </li>
              </ol>

              <p>
                After it's approved, you'll be able to easily share your post across your social media channels and extend your reach.
              </p>
            </div>
        </template>
        <div class="overflow-x-auto">
            <div class="flex flex-col">
                <div class="overflow-x-auto">
                    <div class="align-middle inline-block min-w-full">
                      <div class="overflow-hidden">
                        <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0">
                          <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1">
                            <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of Properties per page">
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </select>
                          </div>
                          <div class="col-span-2 sm:w-3/12 sm:pr-4 order-1 sm:order-2 px-2">
                            <select v-model="form.status" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of Properties per page">
                              <option value="">All</option>
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          </div>
                          <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                            <input id="search" v-model="form.search" type="text" placeholder="Search for blog post..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                            <div class="flex items-center ml-2 mt-2" @click="form.trashed = !form.trashed">
                              <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                  <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                      <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                  </Switch>
                                  <SwitchLabel as="span" class="ml-3 text-sm">
                                      <span class="font-medium text-gray-700">Show Archived Blog Posts</span>
                                  </SwitchLabel>
                              </SwitchGroup>
                            </div>
                          </div>
                        </div>
                        <ul v-if="blogs.data.length > 0" role="list" class=" grid grid-cols-1 gap-6 sm:grid-cols-3 lg:grid-cols-3 my-5 px-2">
                          <li v-for="blog in blogs.data" :key="blog.id"
                              class="col-span-1 divide-y rounded-lg shadow-lg border bg-white divide-gray-200"
                              :class="statusClass(blog.status)"
                          >
                            <div class="relative flex flex-col h-full text-center justify-between gap-x-6 p-6">
                              <img
                                v-if="blog.image_url"
                                :src="blog.image_url"
                                class="rounded w-full h-48 object-cover mb-3"
                              />

                              <h2 class="text-2xl font-semibold leading-6 text-gray-900">
                                {{ blog.title }}
                              </h2>

                              <div v-html="(stripped = blog.description.replace(/<[^>]+>/g, '')) && (stripped.length > 200 ? stripped.slice(0, 200) + '...' : stripped)"></div>

                              <div class="mt-2 text-sm font-medium"
                                  :class="{
                                    'text-green-600': blog.status === 'approved',
                                    'text-yellow-600': blog.status === 'pending',
                                    'text-red-600': blog.status === 'rejected'
                                  }">
                                Status: {{ blog.deleted_at ? 'Archived' : capitalize(blog.status) }}
                              </div>
                              <div v-if="blog.status === 'rejected'" class="mt-2 text-sm font-medium">
                                Reason: {{ blog.rejection_reason }}
                              </div>
                              <template v-if="$page.props.user.is_admin">
                                <div class="mt-2 text-sm font-medium">
                                  Author: {{ blog.user.name }}
                                </div>
                                <div class="mt-2 text-sm font-medium">
                                  Submitted on: {{ blog.created_at }}
                                </div>
                              </template>

                              <div v-if="!blog.deleted_at" class="pt-4 bg-gray-50 flex flex-wrap justify-center gap-3 border-t mt-4">
                                <button
                                  v-if="blog.status === 'approved'"
                                  class="primaryButton"
                                  @click="copyToClipboard(blog.wordpress_post_link)"
                                >
                                  Copy Link
                                </button>

                                <a
                                  v-if="blog.status === 'approved'"
                                  :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(blog.wordpress_post_link)}`"
                                  target="_blank"
                                  class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-1 mt-1 h-[38px]"
                                >
                                  Share on Facebook
                                </a>

                                <a
                                  v-if="blog.status === 'approved'"
                                  :href="blog.wordpress_post_link"
                                  target="_blank"
                                  class="primaryButton"
                                >
                                  View Article
                                </a>

                                <a
                                  v-if="blog.status === 'approved'"
                                  :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(blog.wordpress_post_link)}`"
                                  target="_blank"
                                  class="bg-blue-800 hover:bg-blue-900 text-white px-4 py-2 rounded flex items-center gap-1 mt-1 h-[38px]"
                                >
                                  Share on LinkedIn
                                </a>

                                <inertia-link
                                  v-if="blog.status === 'pending' && $page.props.user.is_admin"
                                  :href="route('blogs.edit', blog.id)"
                                  class="primaryButton"
                                >
                                  Edit
                                </inertia-link>
                                <inertia-link
                                  v-if="blog.status === 'rejected' && !$page.props.user.is_admin"
                                  :href="route('blogs.edit', blog.id)"
                                  class="primaryButton"
                                >
                                  Edit & Resubmit
                                </inertia-link>

                                <button
                                  v-if="!$page.props.user.is_admin"
                                  @click="destroy(blog)"
                                  class="dangerButton"
                                >
                                  Archive
                                </button>

                                <button
                                  v-if="$page.props.user.is_admin && blog.status === 'pending'"
                                  @click="approve(blog.id)"
                                  class="successButton"
                                  :disabled="isApproveButtonDisabled"
                                  :class="{ 'opacity-50 cursor-not-allowed': isApproveButtonDisabled }"
                                >
                                  Approve
                                </button>

                                <button
                                  v-if="$page.props.user.is_admin && blog.status === 'pending'"
                                  class="dangerButton"
                                  @click="openRejectionModal(blog)"
                                >
                                  Reject
                                </button>
                              </div>
                              <div v-else-if="blog.deleted_at && $page.props.user.is_admin" class="pt-4 bg-gray-50 flex flex-wrap justify-center gap-3 border-t mt-4">
                                <inertia-link
                                  :href="route('blogs.edit', blog.id)"
                                  class="primaryButton"
                                >
                                  Edit
                                </inertia-link>
                              </div>
                              <div v-else-if="blog.deleted_at" class="pt-4 bg-gray-50 flex flex-wrap justify-center gap-3 border-t mt-4">
                                <button
                                  v-if="!$page.props.user.is_admin"
                                  @click="restore(blog)"
                                  class="successButton"
                                >
                                  Restore
                                </button>
                              </div>
                            </div>
                          </li>
                        </ul>
                        <div v-else class="rounded bg-yellow-50 border border-t-yellow-800 border-t-4 border-yellow-400 py-4 px-2 mt-4">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <font-awesome-icon :icon="faInfoCircle" class="h-5 w-5 text-yellow-700" aria-hidden="true" />
                                </div>
                                <div class="ml-3 flex-1 md:flex md:justify-between">
                                    <p v-if="form.search === '' && form.status === ''" class="text-sm text-yellow-700">
                                      You haven't created any blog posts yet.
                                    </p>
                                    <p v-else class="text-sm text-yellow-700">
                                      No blogs are available for this search.
                                    </p>
                                </div>
                            </div>
                        </div>
                      </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="blogs.links" :results="blogs" :type="'blog posts'" />
        </div>
        <modal-jetstream :show="showRejectionModal" :max-width="'3xl'">
          <form @submit.prevent="reject">
            <div class="rounded-md overflow-x-auto m-2 mx-4">
                <div
                    class="max-w-3xl mb-5 mx-auto mt-8 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                    <div>
                        <h1 class="text-2xl font-bold">Reject Blog <strong>{{ selectedBlog?.title }}</strong></h1>
                    </div>
                </div>
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden">
                              <div class="sm:col-span-full">
                                <label for="rejection_reason" :class="rejectionForm.errors.rejection_reason ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                    Rejection Reason
                                </label>
                                <div class="mt-1">
                                    <textarea id="rejection_reason" v-model="rejectionForm.rejection_reason" type="text" v-bind:class="(rejectionForm.errors.rejection_reason)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"></textarea>
                                </div>
                                <p class="text-red-500 text-sm" v-text="rejectionForm.errors.rejection_reason"></p>
                              </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-end items-center mt-4">
                  <button @click="showRejectionModal = false" class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">Close</button>
                  <form-button :form="rejectionForm" class="h-[38px]">Submit</form-button>
                </div>
            </div>
          </form>
        </modal-jetstream>
        
        <!-- Deleting Blog -->
        <div v-if="showDeleteModal">
            <TransitionRoot as="template" :show="showDeleteModal">
                <Dialog as="div" class="relative z-50" @close="showDeleteModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">Confirm Archive</DialogTitle>
                                            <div class="mt-3">
                                              Are you sure you want to archive this item? You can undo this action later.
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showDeleteModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="confirmDestroy()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Restore Blog post -->
        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">Confirm Restore</DialogTitle>
                                            <div class="mt-3">
                                              Are you sure you want to restore this blog post?
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="confirmRestore()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowsRotate, faBoxArchive, faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import { router } from '@inertiajs/vue3'
import ModalJetstream from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot, Switch, SwitchGroup, SwitchLabel } from '@headlessui/vue'

export default {
  props: ['blogs', 'filters'],
  data() {
    return {
      form: {
        paginate: (this.filters.paginate)?this.filters.paginate:10,
        search: this.filters.search ?? '',
        status: this.filters.status ?? '',
        trashed: this.filters.trashed,
      },
      showRejectionModal: false,
      selectedBlog: null,
      rejectionForm: this.$inertia.form({
        rejection_reason: ''
      }),
      showDeleteModal: false,
      showRestoreModal: false,
      isApproveButtonDisabled: false,
      faInfoCircle,
      faArrowsRotate,
    }
  },
  components: {
    TailwindLayout,
    FontAwesomeIcon,
    Pagination,
    ModalJetstream,
    FormButton,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    Switch,
    SwitchGroup,
    SwitchLabel
  },
  watch: {
    form: {
        deep: true,
        handler: throttle(function() {
            this.$inertia.get(this.route('blogs.index'), pickBy(this.form), { preserveState: true })
        }, 150),
    },
    },
  methods: {
    approve(id) {
      this.isApproveButtonDisabled = true;
      router.put(route('blogs.approve', id))
    },
    reject() {
      this.rejectionForm.put(this.route('blogs.reject', this.selectedBlog.id), {
        onSuccess: () => {
          console.log('onFinish');
          this.selectedBlog = null;
          this.rejectionForm.reset();
          this.showRejectionModal = false;
        }
      });
    },
    async copyToClipboard(url) {
      try {
          await navigator.clipboard.writeText(url);
          alert('Copied');
      } catch($e) {
          alert('Cannot copy');
      }
    },
    statusClass(status) {
      switch (status) {
        case 'approved': return 'border-green-400';
        case 'pending': return 'border-yellow-400';
        case 'rejected': return 'border-red-400';
        default: return '';
      }
    },
    openRejectionModal(blog) {
      this.selectedBlog = blog;
      this.showRejectionModal = true;
    },
    capitalize(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },

    destroy(blog) {
      this.selectedBlog = blog;
      this.showDeleteModal = true;
    },

    confirmDestroy() {
      this.$inertia.delete(this.route('blogs.destroy', this.selectedBlog.id), {
          onFinish: () => {
              this.showDeleteModal = false;
          }
      })
    },
    restore(blog) {
      this.selectedBlog = blog;
      this.showRestoreModal = true;
    },

    confirmRestore() {
      this.$inertia.put(this.route('blogs.restore', this.selectedBlog.id), {}, {
          onFinish: () => {
              this.showRestoreModal = false;
          }
      })
    },
  }
}
</script>
